import React, { useState, useEffect, useCallback } from "react";
import {
  AnnualTabContainer,
  Container,
  HeadingContainer,
  LoadingContainer,
  PricingContainer,
  SavingsText,
  StyledTab,
  SubscriptionContainer,
  TabContainer,
} from "./styles";
import { CircularProgress, Tabs } from "@mui/material";
import { useGetAllSubcriptions } from "../../../services/queries";
import { PlanComparison } from "../../SubscriptionPlans";
import { SubscriptionFeatures } from "../../SubscriptionPlans/SubscriptionFeatures";
import UpgradeModal from "../../SubscriptionModals/UpgradeModal/UpgradeModal";
import DowngradeModal from "../../SubscriptionModals/DowngradeModal/DowngradeModal";
import SubscriptionItem from "../../Referrals/children/SubscriptionItem";
import { useSubscription } from "../../../hooks/useSubscription";
import { toast } from "react-toastify";
import { isChrome, isSafari } from "../../../utils/browserDetection";
import styled from "styled-components";
import Button from "../../Button";
import TextView from "../../TextView";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

// Styled components for payment redirect modal
const PaymentRedirectModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000002;
`;

const PaymentModalContent = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 32px;
  max-width: 450px;
  width: 90%;
  position: relative;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const PaymentModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const PaymentCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const PaymentRedirectButton = styled(Button)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const SubscriptionPage = ({
  activePlan,
  fromSignup = false,
  subscriptionsData,
}) => {
  // Browser detection
  const browserIsSafari = isSafari();

  const {
    // Constants
    BILLING_CYCLES,
    SUBSCRIPTION_ACTIONS,

    // State
    selectedPlan,
    defaultCardId,
    showUpgradeModal,
    showDowngradeModal,
    loading,
    comparePlan,
    features,
    packages,
    promoCode,
    discountInfo,
    codeError,
    validatingCode,

    // Utility functions
    getSubscriptionLimits,
    extractListItems,
    getSubscriptionPlans,
    getDowngradeWarningItems,

    // Actions
    handlePaymentMethodChange,
    handleApplyDiscountCode,
    handleActionPress,
    handleConfirmAction,
    fetchComparisonData,
    setComparePlan,
    closeAllModals,
  } = useSubscription({
    enabled: true,
  });

  const [pendingUrl, setPendingUrl] = useState(null);
  const [isPaymentRedirectModalOpen, setIsPaymentRedirectModalOpen] = useState(false);

  const tabValueMapping = {
    Monthly: BILLING_CYCLES.MONTHLY,
    Annual: BILLING_CYCLES.ANNUAL,
  };

  const [value, setValue] = useState(
    tabValueMapping[activePlan] || BILLING_CYCLES.ANNUAL
  );

  const isAnnualBilling = value === BILLING_CYCLES.ANNUAL;

  const subscriptionLimit = getSubscriptionLimits(value);

  // Only fetch data if not provided from parent (when fromSignup is false)
  const { data, isLoading, refetch } = useGetAllSubcriptions(
    subscriptionLimit,
    { enabled: !fromSignup || !subscriptionsData }
  );

  // Use provided data when fromSignup is true, otherwise use fetched data
  const subscriptionData =
    fromSignup && subscriptionsData ? subscriptionsData : data;
  const isLoadingData = fromSignup && subscriptionsData ? false : isLoading;

  const tabs = ["Annual", "Monthly"];

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const openComparisonTable = async () => {
    await fetchComparisonData();
  };

  // Payment handling functions from UpgradePopup
  const handleOpenPaymentPage = () => {
    window.open(pendingUrl, "_blank");
    closeAllModals();
  };

  const handleClosePaymentPopup = () => {
    setPendingUrl(null);
    setIsPaymentRedirectModalOpen(false);
  };

  // Custom onSuccess and onError handlers for this component
  const onSubscriptionSuccess = useCallback(
    (res) => {
   
      if (res?.data?.data?.url) {
        // Store the URL for user to open manually to avoid popup blockers
        if (!browserIsSafari) {
          handleOpenPaymentPage();
        } else {
          setPendingUrl(res.data.data.url);
          setIsPaymentRedirectModalOpen(true);
        }
      } else {
        closeAllModals();
        toast.success("Subscription updated successfully");
      }
      refetch();
    },
    [closeAllModals, refetch, browserIsSafari]
  );

  const onSubscriptionError = useCallback((error) => {
    console.error("Subscription error:", error);
    toast.error("Failed to process subscription");
  }, []);

  // Custom confirm handler that includes our specific callbacks
  const handleUpgradeConfirm = useCallback(
    (params = {}) => {

      handleConfirmAction({
        ...params,
        source: fromSignup ? "onboarding" : "settings",
        onSuccess: onSubscriptionSuccess,
        onError: onSubscriptionError,
      });
    },
    [handleConfirmAction, onSubscriptionSuccess, onSubscriptionError, fromSignup]
  );

  const renderSubscriptionItems = () => {
    if (!subscriptionData?.data?.data?.length) return null;

    return subscriptionData.data.data.map((item) => {
      const listItems = extractListItems(item.body);
      return (
        <SubscriptionContainer key={item.id}>
          <SubscriptionItem
            key={item.id}
            title={item.title}
            price={item.price}
            listItems={listItems}
            item={item}
            onViewFeatures={() => openComparisonTable()}
            onActionPress={() => handleActionPress(item)}
            showFeatures={!comparePlan}
            isAnnually={isAnnualBilling}
          />
          {comparePlan && (
            <SubscriptionFeatures
              features={features}
              packages={packages}
              subscription={item}
              closeComparison={() => setComparePlan(false)}
            />
          )}
        </SubscriptionContainer>
      );
    });
  };

  const renderTabLabel = (tabName) => {
    if (tabName === "Annual") {
      return (
        <AnnualTabContainer>
          <span>{tabName}</span>
          <SavingsText>Savings</SavingsText>
        </AnnualTabContainer>
      );
    }
    return tabName;
  };

  // Get the downgrade warning items
  const downgradeWarningItems = getDowngradeWarningItems();

  // Get plan data
  const { fromPlanData, toPlanData, freePlanData } = getSubscriptionPlans(
    true,
    true,
    selectedPlan?.action === SUBSCRIPTION_ACTIONS.CANCEL
  );

  return (
    <Container style={{ position: "relative" }}>
      <TabContainer>
        <Tabs
          value={value}
          onChange={handleChange}
          indicatorColor="transparent"
        >
          {tabs.map((tab, index) => (
            <StyledTab key={index} label={renderTabLabel(tab)} />
          ))}
        </Tabs>
      </TabContainer>

      <PricingContainer>
        {isLoadingData && !fromSignup && (
          <LoadingContainer>
            <CircularProgress />
          </LoadingContainer>
        )}

        {comparePlan && <PlanComparison features={features} />}

        {!isLoadingData && renderSubscriptionItems()}
      </PricingContainer>

      {/* Upgrade Modal - only render if showUpgradeModal is true and we have plans */}
      {showUpgradeModal && selectedPlan && (
        <UpgradeModal
          open={showUpgradeModal}
          onClose={closeAllModals}
          onConfirm={handleUpgradeConfirm}
          fromPlan={fromPlanData}
          toPlan={toPlanData}
          isPendingPayment={loading}
          handlePaymentMethodChange={handlePaymentMethodChange}
          upgradeButtonText={
            selectedPlan.action === SUBSCRIPTION_ACTIONS.UPGRADE
              ? "Upgrade"
              : "Subscribe"
          }
          loading={loading}
          // Disable action button if there's no payment method and it's not a cancellation
          // isActionDisabled={!defaultCardId && selectedPlan.action !== SUBSCRIPTION_ACTIONS.CANCEL}
          showRefundInfo={fromPlanData?.type !== "free"}
          // Promo code props
          promoCode={promoCode}
          discountInfo={discountInfo}
          codeError={codeError}
          validatingCode={validatingCode}
          onApplyPromoCode={handleApplyDiscountCode}
        />
      )}

      {/* Payment Redirect Popup */}
      {isPaymentRedirectModalOpen && (
        <PaymentRedirectModal>
          <PaymentModalContent>
            <PaymentModalHeader>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <AutoAwesomeIcon
                  style={{ color: " #F5CC3B", fontSize: 24, marginRight: 5 }}
                />
                <TextView variant="subtitle">Complete Your Payment</TextView>
              </div>
              <div></div>
              <PaymentCloseButton onClick={handleClosePaymentPopup}>
                <CloseIcon />
              </PaymentCloseButton>
            </PaymentModalHeader>
            <TextView style={{ marginBottom: "24px", color: "#666" }}>
              Your subscription is almost ready! Click the button below to
              redirect to the payment screen and complete your purchase.
            </TextView>
            <PaymentRedirectButton onClick={handleOpenPaymentPage}>
              Redirect to Payment Screen
            </PaymentRedirectButton>
          </PaymentModalContent>
        </PaymentRedirectModal>
      )}

      {/* Downgrade Modal - only render if showDowngradeModal is true and we have plans */}
      {showDowngradeModal && selectedPlan && (
        <DowngradeModal
          open={showDowngradeModal}
          onClose={closeAllModals}
          onConfirm={handleConfirmAction}
          handlePaymentMethodChange={handlePaymentMethodChange}
          fromPlan={fromPlanData}
          toPlan={
            selectedPlan.action === SUBSCRIPTION_ACTIONS.CANCEL
              ? freePlanData
              : toPlanData
          }
          warningItems={downgradeWarningItems}
          showPaymentCard={selectedPlan.action !== SUBSCRIPTION_ACTIONS.CANCEL}
          loading={loading}
          // Disable action button if there's no payment method and it's not a cancellation
          isActionDisabled={
            !defaultCardId &&
            selectedPlan.action !== SUBSCRIPTION_ACTIONS.CANCEL
          }
          downgradeButtonText={
            selectedPlan.action === SUBSCRIPTION_ACTIONS.CANCEL
              ? "Cancel Subscription"
              : "Downgrade"
          }
          // Promo code props
          promoCode={promoCode}
          discountInfo={discountInfo}
          codeError={codeError}
          validatingCode={validatingCode}
          onApplyPromoCode={handleApplyDiscountCode}
        />
      )}
    </Container>
  );
};
